/* Presentational identity form bodies — split out of IdentityEditDialog so
 * the dialog stays focused on orchestration (workflow runs + guards). Both
 * are fully visual (no JSON): the user form edits email/name/password/roles,
 * the role form edits name/journeys. State lives in the parent. */
import React from 'react';
import {
    AlertTriangle,
    Loader2,
    Lock,
    Trash2,
    UserX
} from 'lucide-react';
import ChipMultiSelect from '../../shared/forms/ChipMultiSelect';
import type {
    ChipOption
} from '../../shared/forms/ChipMultiSelect';
import type {
    WorkflowFailure
} from '../../api/appEngine.service';

export function UserForm({
    creating,
    email,
    name,
    password,
    roles,
    roleOptions,
    onEmail,
    onName,
    onPassword,
    onRoles
}: {
    creating: boolean;
    email: string;
    name: string;
    password: string;
    roles: string[];
    roleOptions: ChipOption[];
    onEmail: (v: string) => void;
    onName: (v: string) => void;
    onPassword: (v: string) => void;
    onRoles: (v: string[]) => void;
}) {
    return (
        <>
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12
            }}>
                <div>
                    <label className="aps-label">Email</label>
                    <input className="aps-input" type="email" value={email}
                        placeholder="you@org.com"
                        onChange={(e) => onEmail(e.target.value)} />
                </div>
                <div>
                    <label className="aps-label">Name</label>
                    <input className="aps-input" value={name}
                        placeholder="optional"
                        onChange={(e) => onName(e.target.value)} />
                </div>
            </div>
            <div>
                <label className="aps-label">Password</label>
                <input className="aps-input" type="password" value={password}
                    autoComplete="new-password"
                    placeholder={creating
                        ? 'Set a password'
                        : 'Leave blank to keep the current password'}
                    onChange={(e) => onPassword(e.target.value)} />
                <p className="aps-help">
                    {creating
                        ? 'Stored hashed — never in plaintext.'
                        : 'Blank keeps the stored hash; type a new value to change it.'}
                </p>
            </div>
            <div>
                <label className="aps-label">Roles</label>
                <ChipMultiSelect
                    options={roleOptions}
                    value={roles}
                    onChange={onRoles}
                    placeholder="No roles — pick one or more"
                    emptyHint="No roles yet — create one in the Roles tab." />
            </div>
        </>
    );
}

export function RoleForm({
    creating,
    structural,
    docId,
    name,
    journeys,
    journeyOptions,
    onName,
    onJourneys
}: {
    creating: boolean;
    structural: boolean;
    docId: string;
    name: string;
    journeys: string[];
    journeyOptions: ChipOption[];
    onName: (v: string) => void;
    onJourneys: (v: string[]) => void;
}) {
    return (
        <>
            <div>
                <label className="aps-label">Role name</label>
                <input className="aps-input" value={name}
                    placeholder="Manager"
                    onChange={(e) => onName(e.target.value)} />
                {structural && (
                    <p className="aps-help" style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5
                    }}>
                        <Lock size={11} />
                        Structural role — it cannot be deleted.
                    </p>
                )}
                {!creating && (
                    <p className="aps-help aps-mono">id: {docId}</p>
                )}
            </div>
            <div>
                <label className="aps-label">
                    Journeys (the menu &#8801; the gate)
                </label>
                <ChipMultiSelect
                    options={journeyOptions}
                    value={journeys}
                    onChange={onJourneys}
                    placeholder="No journeys granted"
                    emptyHint="No journeys yet — author one in Journeys first." />
                <p className="aps-help">
                    Granting a journey to a role publishes it — the whole list is
                    set as you check/uncheck.
                </p>
            </div>
        </>
    );
}

/** The two delete notices: a role-referenced CONFLICT (offer force-clean,
 * with the holders listed) and a plain delete CONFIRM. Closed-outcome
 * driven — only one shows at a time. */
export function DeleteNotices({
    isUsers,
    title,
    busy,
    conflict,
    confirmDelete,
    onCancelConfirm,
    onKeepRole,
    onDelete,
    onForceDelete
}: {
    isUsers: boolean;
    title: string;
    busy: boolean;
    conflict: WorkflowFailure | null;
    confirmDelete: boolean;
    onCancelConfirm: () => void;
    onKeepRole: () => void;
    onDelete: () => void;
    onForceDelete: () => void;
}) {
    const referencedCount = (conflict && typeof conflict.count === 'number')
        ? conflict.count
        : (conflict && Array.isArray(conflict.referenced))
            ? conflict.referenced.length
            : 0;
    return (
        <>
            {conflict && (
                <div className="aps-errors" style={{
                    borderColor: 'var(--aps-warning)'
                }}>
                    <p className="aps-errors-title" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                    }}>
                        <AlertTriangle size={14} color="var(--aps-warning)" />
                        {referencedCount > 0
                            ? `${referencedCount} user${referencedCount === 1
                                ? '' : 's'} hold this role`
                            : 'This role is still referenced'}
                    </p>
                    {Array.isArray(conflict.referenced)
                        && conflict.referenced.length > 0 && (
                        <ul>
                            {conflict.referenced.map((u: any, i: number) => (
                                <li key={i} className="aps-mono">
                                    {String(u?.email || u?.name || u?.id || u)}
                                </li>
                            ))}
                        </ul>
                    )}
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        marginTop: 8
                    }}>
                        <button className="aps-btn aps-btn-ghost" disabled={busy}
                            onClick={onKeepRole}>
                            Keep role
                        </button>
                        <button className="aps-btn aps-btn-danger" disabled={busy}
                            onClick={onForceDelete}>
                            <UserX size={14} />
                            Force remove (also strips it from those users)
                        </button>
                    </div>
                </div>
            )}
            {confirmDelete && !conflict && (
                <div className="aps-errors" style={{
                    borderColor: 'var(--aps-danger)'
                }}>
                    <p className="aps-errors-title">
                        Delete {isUsers ? 'user' : 'role'} {title}?
                    </p>
                    <ul>
                        <li>
                            {isUsers
                                ? 'This removes the user record. Sessions already '
                                  + 'issued keep their copied context until they expire.'
                                : 'This removes the role. It is blocked while users '
                                  + 'still hold it (force-clean is offered if so).'}
                        </li>
                    </ul>
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        marginTop: 8
                    }}>
                        <button className="aps-btn aps-btn-ghost" disabled={busy}
                            onClick={onCancelConfirm}>
                            Cancel
                        </button>
                        <button className="aps-btn aps-btn-danger" disabled={busy}
                            onClick={onDelete}>
                            {busy
                                ? <Loader2 size={15} className="aps-spin" />
                                : <Trash2 size={15} />}
                            Delete
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
